import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { DataTable } from '../../../../components/ui/DataTable';
import { CopyableText } from '../../../../components/ui/CopyableText';
import { PrinterX, Inbox, Truck } from 'lucide-react';
import { getDeliveries, fetchDeliveryFilterOptions } from '../../../../features/deliveries/deliveriesThunks';
import { setFilters } from '../../../../features/deliveries/deliveriesSlice';
import { useSelectedOrderIds } from '../../../../features/deliveries/useSelectedOrderIds';
import { formatCourier } from '../../../../features/deliveries/deliveriesUtils';
import { fetchPartnerships } from '../../../../features/partnerships/partnershipsThunks';
import { UPSELLING_HIDDEN } from '../../../../features/upselling/upsellingConstants';
import { selectHasAdvancedFilter, selectHasFulfillmentPro, selectHasPickupPoints, selectIsCheckoutPlan } from '../../../../features/auth/authSelectors';
import { EMPTY_ADVANCED_FILTER, isAdvancedFilterActive } from '../../../../features/deliveries/advancedFilter';
import { EDITABLE_ITEM_STATUSES } from '../../../../features/deliveryItems/deliveryItemsConstants';

// Subcomponents
import { DeliveryChips } from '../../../../components/deliveries/DeliveryChips';
import { DestinationCell } from '../../../../components/deliveries/DestinationCell';
import { StatusBadge } from '../../../../components/deliveries/StatusBadge';
import { ItemsCountCell } from '../../../../components/deliveries/ItemsCountCell';
import { DeliveryFilters } from './components/DeliveryFilters';
import { DeliveryActions } from './components/DeliveryActions';
import { UpsellChip } from './components/UpsellChip';

// Modals
import { MassiveChangeStatusModal } from '../../../../components/deliveries/MassiveChangeStatusModal';
import { ChangeDriverModal } from '../../../../components/deliveries/ChangeDriverModal';
import { UpsellDeliveriesModal } from '../../../../components/deliveries/UpsellDeliveriesModal';
import { SelectionActions } from '../../../../components/deliveries/SelectionActions';
import { DispatchToCourierModal } from '../../../../components/deliveries/DispatchToCourierModal';
import { ChangeCourierBulkModal } from '../../../../components/deliveries/ChangeCourierBulkModal';
import { ChangePickupPointBulkModal } from '../../../../components/deliveries/ChangePickupPointBulkModal';
import { ManageBultosModal } from '../../../../components/deliveries/ManageBultosModal';

export const Deliveries = () => {
  const dispatch = useDispatch();
  const { data, total, statusCount, globalTotal, globalStatusCount, isLoading, filters, companyOptions, error } = useSelector(state => state.deliveries);
  const { partnerships } = useSelector(state => state.partnerships);
  const { user } = useSelector(state => state.auth);
  const isAdmin = user?.user_type?.includes('admin');
  // Only the checkout plan assigns drivers, so only there does filtering by one
  // mean anything.
  //
  // Which means this is false for everyone who reaches this screen: an account
  // on that plan is handed over to the old front before the admin tree mounts
  // (see `legacyProfileOf`), and `selectIsCheckoutPlan` reads the signed-in
  // session's own plan.
  //
  // The staff's copy of this listing is the one place the filter still appears,
  // and it gets there by a different route: it asks `selectedAdmin?.planType`,
  // the plan of the operator being looked at, which is the right question and
  // the one this line cannot ask. Either the filter follows the plan out of
  // here, or the handover stops covering that plan — a product call, not a
  // cleanup.
  const isCheckoutPlan = useSelector(selectIsCheckoutPlan);
  const hasFulfillmentPro = useSelector(selectHasFulfillmentPro);
  const hasPickupPoints = useSelector(selectHasPickupPoints);
  const hasAdvancedFilter = useSelector(selectHasAdvancedFilter);

  // Table selection state
  const [selectedRows, setSelectedRows] = useState([]);

  // Modals state
  const [isChangeStatusOpen, setIsChangeStatusOpen] = useState(false);
  const [isChangeDriverOpen, setIsChangeDriverOpen] = useState(false);
  const [isUpsellOpen, setIsUpsellOpen] = useState(false);
  const [isDispatchOpen, setIsDispatchOpen] = useState(false);
  const [isChangeCourierOpen, setIsChangeCourierOpen] = useState(false);
  const [isChangePickupPointOpen, setIsChangePickupPointOpen] = useState(false);
  // The row whose bultos are being re-counted; one modal serves the whole table.
  const [itemsCountRow, setItemsCountRow] = useState(null);

  const selectedOrderIds = useSelectedOrderIds(data, selectedRows);

  // The filters are persisted (`persist:deliveries`), so a browser that filtered
  // by driver while the account had the checkout plan would come back to a table
  // narrowed by a select that is no longer on screen. Same reason the seller
  // neutralises the filters it does not show.
  const driverFilterStuck = !isCheckoutPlan && filters.assignedDriverFilter !== 'all';
  // The same with the upselling filter while upselling is hidden.
  const upsellFilterStuck = UPSELLING_HIDDEN && (filters.upsell_filter ?? 'all') !== 'all';
  // And with an advanced filter persisted by a plan that no longer has the
  // addon: the backend would refuse the whole listing (403).
  const advancedFilterStuck = !hasAdvancedFilter && isAdvancedFilterActive(filters);
  const filterStuck = driverFilterStuck || upsellFilterStuck || advancedFilterStuck;

  useEffect(() => {
    if (driverFilterStuck) dispatch(setFilters({ assignedDriverFilter: 'all', page: 1 }));
  }, [dispatch, driverFilterStuck]);

  useEffect(() => {
    if (upsellFilterStuck) dispatch(setFilters({ upsell_filter: 'all', page: 1 }));
  }, [dispatch, upsellFilterStuck]);

  useEffect(() => {
    if (advancedFilterStuck) dispatch(setFilters({ ...EMPTY_ADVANCED_FILTER, page: 1 }));
  }, [dispatch, advancedFilterStuck]);

  // Fetch cuando cambian los filtros relevantes
  useEffect(() => {
    if (filterStuck) return;
    dispatch(getDeliveries(filters));
  }, [
    filterStuck,
    dispatch,
    filters.start_date,
    filters.end_date,
    filters.company,
    filters.search,
    filters.delivery_status,
    filters.assignedDriverFilter,
    filters.upsell_filter,
    filters.advanced_filter_field,
    filters.advanced_filter_values,
    filters.page,
    filters.rowsPerPage
  ]);

  // The alliances are fetched once so a ceded shipment can name its partner:
  // the listing sends `upsell_partner_id`, not the name. Not while upselling is hidden.
  useEffect(() => {
    if (!UPSELLING_HIDDEN && isAdmin && user?.admin?.id) dispatch(fetchPartnerships(user.admin.id));
  }, [isAdmin, user?.admin?.id, dispatch]);

  // The company filter needs ids, not names. Guarded by its own `condition`, so
  // this is a no-op once the catalog is in.
  useEffect(() => {
    if (isAdmin) dispatch(fetchDeliveryFilterOptions());
  }, [isAdmin, dispatch]);

  // Resolved against the outgoing alliances, where the partner is the target.
  const partnerNameById = useMemo(() => {
    const byId = {};
    partnerships.forEach((item) => {
      if (item.role === 'SOURCE' && item.target_admin_details?.id) {
        byId[item.target_admin_details.id] = item.partner_name;
      }
    });
    return byId;
  }, [partnerships]);

  /**
   * Changing a filter goes back to the first page. Changing the page must not:
   * `{ [field]: value, page: 1 }` writes the same key twice when the field *is*
   * the page, the literal wins, and the listing never leaves page 1.
   */
  const handleFilterChange = (fieldOrUpdates, value) => {
    if (typeof fieldOrUpdates === 'object' && fieldOrUpdates !== null) {
      dispatch(setFilters({
        ...fieldOrUpdates,
        page: 1
      }));
    } else if (fieldOrUpdates === 'page') {
      dispatch(setFilters({ page: value }));
    } else {
      dispatch(setFilters({
        [fieldOrUpdates]: value,
        page: 1
      }));
    }
  };

  const refreshData = () => {
    dispatch(getDeliveries(filters));
    setSelectedRows([]);
  };

  const canEditItemsCount = (row) =>
    hasFulfillmentPro && EDITABLE_ITEM_STATUSES.includes(row.status) && !row.is_external;

  const showUpsellMarks = !UPSELLING_HIDDEN;

  const columns = [
    {
      header: 'ID & INFO',
      cell: (row) => (
        <div className="flex flex-col gap-1">
          <Link
            to={`/order-details/${row.order_id}`}
            title="Ver Detalles"
            className="font-bold text-ninja-dark dark:text-white hover:text-ninja-green hover:underline transition-colors w-fit"
          >
            #{row.order_id}
          </Link>
          {/* Stacked rather than in a row: side by side, the column measured
              the sum of every chip instead of just the widest one. */}
          <div className="flex flex-col items-start gap-1 mt-1">
            {!row.has_label && (
              <div title="Sin etiqueta" className="flex items-center gap-1 bg-red-50 dark:bg-red-900/20 text-red-500 px-1.5 py-0.5 rounded text-[10px] font-bold border border-red-100 dark:border-red-900/50">
                <PrinterX size={12} />
                <span>SIN ETIQUETA</span>
              </div>
            )}
            {/* The upselling marks — received from a partner, ceded to one — go while it is hidden. */}
            {showUpsellMarks && row.is_external && (
              <div title="Envío asignado a mi" className="flex items-center gap-1 bg-blue-50 dark:bg-blue-900/20 text-blue-500 px-1.5 py-0.5 rounded text-[10px] font-bold border border-blue-100 dark:border-blue-900/50">
                <Inbox size={12} />
              </div>
            )}
            {showUpsellMarks && row.is_assigned && (
              <UpsellChip row={row} partnerName={partnerNameById[row.upsell_partner_id]} />
            )}
            {/* Si no hay iconos y queremos evitar que la fila colapse su altura, ponemos un espacio invisible */}
            {(row.has_label && !(showUpsellMarks && (row.is_external || row.is_assigned))) && <span className="h-[20px]"></span>}
          </div>
        </div>
      )
    },
    {
      header: 'DETALLES',
      cell: (row) => (
        <div className="flex flex-col">
          <CopyableText
            value={row.reference_id}
            maxWidth="180px"
            wrap
            className="font-medium text-ninja-dark dark:text-white"
          />
          <ItemsCountCell
            count={row.total_items}
            editable={canEditItemsCount(row)}
            onEdit={() => setItemsCountRow(row)}
          />
        </div>
      )
    },
    {
      header: 'DESTINO',
      cell: (row) => <DestinationCell row={row} />
    },
    {
      header: 'LOGÍSTICA',
      cell: (row) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5 font-medium text-ninja-dark dark:text-white">
            <Truck size={14} className="text-text-muted" />
            <span>{formatCourier(row.courier)}</span>
          </div>
          <div className="mt-0.5">
            <CopyableText
              value={row.tracking_number}
              maxWidth="105px"
              className="text-xs text-ninja-green font-medium"
              emptyLabel="Sin tracking"
              emptyClassName="text-xs text-text-secondary"
            />
          </div>
        </div>
      )
    },
    { header: 'SELLER', accessor: 'seller_name' },
    {
      header: 'ESTADO',
      cell: (row) => <StatusBadge status={row.status} testId="delivery-status" />
    },
    // Apart from the status: the date is the order's, and stacked under a badge
    // that moves on its own it read as the date of the status.
    {
      header: 'CREADA',
      cell: (row) => (
        <span className="text-xs font-medium text-text-secondary dark:text-gray-400">{row.created_at || '-'}</span>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <DeliveryChips
        total={globalTotal > 0 ? globalTotal : total}
        statusCount={globalStatusCount?.length > 0 ? globalStatusCount : statusCount}
        currentStatus={filters.delivery_status}
        onStatusChange={(status) => handleFilterChange('delivery_status', status)}
      />

      <DeliveryFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        isAdmin={isAdmin}
        canFilterByDriver={isCheckoutPlan}
        canFilterByUpsell={!UPSELLING_HIDDEN}
        canUseAdvancedFilter={hasAdvancedFilter}
        companyOptions={companyOptions}
      />

      <div className="flex flex-col gap-4 mt-6">
        <div className="min-h-[48px] flex items-center justify-between transition-all duration-300">
          <h2 className="text-base font-bold text-ninja-dark dark:text-white">
            Ordenes
          </h2>

          <DeliveryActions
            user={user}
            isAdmin={isAdmin}
            filters={filters}
          />
        </div>

        <SelectionActions
          user={user}
          selectedRows={selectedRows}
          selectedOrderIds={selectedOrderIds}
          canChangeStatus={isAdmin}
          // False for everyone who gets this far: an account on the checkout
          // plan is handed over to the old front before this tree mounts. Kept
          // until somebody decides which of the two rules gives way.
          canAssignDriver={isAdmin && isCheckoutPlan}
          canCede={isAdmin && !UPSELLING_HIDDEN}
          canDispatch={hasFulfillmentPro}
          // Choosing who carries the parcel is what Fulfillment Pro sells, as in the order detail.
          canChangeCourier={hasFulfillmentPro}
          // The points are the operator's, and it is the operator who receives the parcels there.
          canChangePickupPoint={isAdmin && hasPickupPoints}
          onOpenChangeStatusModal={() => setIsChangeStatusOpen(true)}
          onOpenChangeDriverModal={() => setIsChangeDriverOpen(true)}
          onOpenUpsellModal={() => setIsUpsellOpen(true)}
          onOpenDispatchModal={() => setIsDispatchOpen(true)}
          onOpenChangeCourierModal={() => setIsChangeCourierOpen(true)}
          onOpenChangePickupPointModal={() => setIsChangePickupPointOpen(true)}
          onFulfilled={refreshData}
        />


        <DataTable
          columns={columns}
          data={data}
          totalItems={total}
          currentPage={filters.page}
          itemsPerPage={filters.rowsPerPage}
          isLoading={isLoading}
          onPageChange={(page) => handleFilterChange('page', page)}
          onRowsPerPageChange={(rowsPerPage) => {
            dispatch(setFilters({ rowsPerPage, page: 1 }));
          }}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          idAccessor="delivery_id"
          error={error}
          onRetry={() => dispatch(getDeliveries(filters))}
        />
      </div>

      {/* Order ids, like the upsell modal: `deliveries/change_status/` filters
          with `order__id__in`, and this used to hand it delivery ids. */}
      <MassiveChangeStatusModal
        isOpen={isChangeStatusOpen}
        onClose={() => setIsChangeStatusOpen(false)}
        selectedOrderIds={selectedOrderIds}
        onSuccess={refreshData}
      />

      {/* Order ids too: `deliveries/change_driver/` filters with `order__id__in`,
          and this used to hand it delivery ids. */}
      <ChangeDriverModal
        isOpen={isChangeDriverOpen}
        onClose={() => setIsChangeDriverOpen(false)}
        selectedDeliveries={selectedOrderIds}
        onSuccess={refreshData}
      />

      {!UPSELLING_HIDDEN && (
        <UpsellDeliveriesModal
          isOpen={isUpsellOpen}
          onClose={() => setIsUpsellOpen(false)}
          selectedOrderIds={selectedOrderIds}
          onSuccess={refreshData}
        />
      )}

      {/* Delivery ids, unlike the modals above: the table already selects them. */}
      <DispatchToCourierModal
        isOpen={isDispatchOpen}
        onClose={() => setIsDispatchOpen(false)}
        deliveryIds={selectedRows}
        onFinished={(failedIds) => {
          dispatch(getDeliveries(filters));
          // What did not go out stays selected, ready for a retry.
          setSelectedRows(failedIds);
        }}
      />

      {/* Delivery ids too: the backend sorts out, row by row, what can change. */}
      <ChangeCourierBulkModal
        isOpen={isChangeCourierOpen}
        onClose={() => setIsChangeCourierOpen(false)}
        deliveryIds={selectedRows}
        onFinished={(failedIds) => {
          dispatch(getDeliveries(filters));
          setSelectedRows(failedIds);
        }}
      />

      {/* Delivery ids too: the backend sorts out, row by row, what can go to the point. */}
      <ChangePickupPointBulkModal
        isOpen={isChangePickupPointOpen}
        onClose={() => setIsChangePickupPointOpen(false)}
        deliveryIds={selectedRows}
        onFinished={(failedIds) => {
          dispatch(getDeliveries(filters));
          setSelectedRows(failedIds);
        }}
      />

      {/* Count and, with Fulfillment Pro, the logistics of each bulto: the
          same rules as the order detail's "Bultos" tab, without leaving the list. */}
      <ManageBultosModal
        isOpen={itemsCountRow !== null}
        onClose={() => setItemsCountRow(null)}
        row={itemsCountRow}
      />
    </div>
  );
};

